/*
  The stage gradient (d-47, d-48: skylrk.com's shaders): one WebGL canvas, fixed to the viewport, inside
  the home or product stage, which clips it (clip-path) and keeps its CSS gradient under it until the
  first frame.
  - Four tones from the stage colour in HSL, as skylrk's: light, secondary, accent, dark. A new piece's
    tones ease in over 600ms.
  - The stage's second colour (--stage-2, colorway-2), when it differs: a soft ribbon round a point the
    stage's CSS sets (--glow-x, --glow-y, shares of the viewport), two ribbons warped by noise,
    stretched, turning and waving slowly out of step (colorway-2-flow), and masked off the bottom-left corner where the text
    sits. It eases in and out with the tones.
  - On a light stage (ink text) the bottom-left corner never goes darker than the stage colour, so the
    label and the name keep their contrast; the dark shades stay everywhere else. Text
    stays readable on its own backings (drop-stage.css, section-main-product-stage.css), not here.
  - Two passes: the gradient less a hash grain (uGrain) into a texture, then that texture plus an
    animated gaussian grain weighted by 1 - colour (uNoise). Buffer: viewport x min(dpr, 2).
  - Draws at ~30fps only while the stage shows, the tab is visible and no overlay is open; one still frame
    under reduced motion. No WebGL, a lost context or a shader error: the CSS gradient stays.
*/
if (!customElements.get('stage-canvas')) {
  const HEAD = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 v;uniform float uTime;`;
  const VERTEX = `attribute vec2 p;varying vec2 v;void main(){v=p*.5+.5;gl_Position=vec4(p,0.,1.);}`;
  const GRADIENT = `${HEAD}
uniform float uGrain,uProtect;
uniform vec2 uRes;
uniform vec3 uLight,uDark,uAccent,uSecond,uGlow,uGlowAt;
mat2 rot(float a){float s=sin(a),c=cos(a);return mat2(c,-s,s,c);}
vec2 hash(vec2 p){p=vec2(dot(p,vec2(2127.1,81.17)),dot(p,vec2(1269.5,283.37)));return fract(sin(p)*43758.5453);}
float noise(vec2 p){vec2 i=floor(p),f=fract(p),u=f*f*(3.-2.*f);
return .5+.5*mix(mix(dot(hash(i)*2.-1.,f),dot(hash(i+vec2(1.,0.))*2.-1.,f-vec2(1.,0.)),u.x),
mix(dot(hash(i+vec2(0.,1.))*2.-1.,f-vec2(0.,1.)),dot(hash(i+1.)*2.-1.,f-1.),u.x),u.y);}
void main(){
float ar=uRes.x/uRes.y,t=uTime*.5;
vec2 p=v-.5;
float d=noise(vec2(t*.05,p.x*p.y));
p.y/=ar;p*=rot(radians((d-.5)*720.+180.));p.y*=ar;
p.x+=sin(p.y*5.+t*2.)/30.;
p.y+=sin(p.x*7.5+t*2.)/15.;
float x=smoothstep(-.3,.2,(p*rot(radians(-5.))).x);
vec3 c=mix(mix(uAccent,uDark,x),mix(uSecond,uLight,x),smoothstep(.5,-.3,p.y));
vec2 g=vec2((v.x-uGlowAt.x)*ar,v.y-uGlowAt.y);
g+=(vec2(noise(g*1.4+t*.12),noise(g*1.4+vec2(4.7,1.3)-t*.1))-.5)*.9;
vec2 a=g*rot(radians(35.+sin(t*.23)*40.));a.x*=.32;a.y+=sin(a.x*9.+t*.9)*.07;
vec2 b=(g+vec2(.28,.16))*rot(radians(-20.+cos(t*.19)*35.));b.x*=.28;b.y+=sin(b.x*11.-t*.8)*.05;
float m=max(1.-smoothstep(0.,.46,length(a)),.65*(1.-smoothstep(0.,.3,length(b))));
m*=smoothstep(.12,.5,v.x*.6+v.y*.4);
c=mix(c,uGlow,uGlowAt.z*m);
c=mix(c,max(c,uAccent),uProtect*(1.-smoothstep(.1,.45,v.x*.6+v.y*.4)));
gl_FragColor=vec4(c-length(hash(v))*uGrain,1.);
}`;
  const GRAIN = `${HEAD}
uniform sampler2D uTex;
uniform float uNoise;
void main(){
vec3 c=texture2D(uTex,v).rgb;
float n=fract(sin(dot(v,vec2(12.9898,78.233)))*43758.5453+uTime*2.);
gl_FragColor=vec4(c+1.596*exp(-n*n*8.)*(1.-c)*uNoise,1.);
}`;
  // The last two are the glow: its colour, and its place and strength (x, y from the bottom, 0 or 0.9).
  const TONES = ['uAccent', 'uDark', 'uSecond', 'uLight', 'uGlow', 'uGlowAt'];

  const rgb = (value) => {
    const parts = (value.match(/[\d.]+/g) || []).slice(0, 3).map(Number);
    return value.startsWith('color(') ? parts : parts.map((part) => part / 255);
  };
  // HSL in and out, all 0-1.
  const hsl = ([r, g, b]) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (!d) return [0, 0, l];
    const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [h / 6, l > 0.5 ? d / (2 - max - min) : d / (max + min), l];
  };
  const fromHsl = (h, s, l) => {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [h + 1 / 3, h, h - 1 / 3].map((t) => {
      t = (t + 1) % 1;
      return t < 1 / 6 ? p + (q - p) * 6 * t : t < 0.5 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
    });
  };
  const mix = (a, b, t) => a.map((part, i) => part + (b[i] - part) * t);

  customElements.define(
    'stage-canvas',
    class StageCanvas extends HTMLElement {
      connectedCallback() {
        this.stage = this.parentElement;
        // The launch brings a new stage's canvas: one per stage.
        this.stage.querySelectorAll(':scope > stage-canvas').forEach((other) => other !== this && other.remove());
        this.holds = new Set(['away']);
        this.frame = this.frame.bind(this);
        this.update = this.update.bind(this);
        this.recolour = this.recolour.bind(this);
        this.resize = this.resize.bind(this);
        this.view = new IntersectionObserver(([entry]) => {
          this.hold('away', !entry.isIntersecting);
          if (entry.isIntersecting) this.init();
        });
        this.watch = new MutationObserver(() => this.hold('overlay', 'overlay' in document.documentElement.dataset));
        this.colours = new MutationObserver(this.recolour);
        this.onVisibility = () => this.hold('hidden', document.hidden);
        this.view.observe(this.stage);
        this.watch.observe(document.documentElement, { attributes: true, attributeFilter: ['data-overlay'] });
        this.colours.observe(this.stage, { attributes: true, attributeFilter: ['style'] });
        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('resize', this.resize);
        reducedMotionQuery.addEventListener('change', this.update);
        this.watch.takeRecords();
        this.hold('overlay', 'overlay' in document.documentElement.dataset);
        this.onVisibility();
      }

      disconnectedCallback() {
        this.view.disconnect();
        this.watch.disconnect();
        this.colours.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
        window.removeEventListener('resize', this.resize);
        reducedMotionQuery.removeEventListener('change', this.update);
        cancelAnimationFrame(this.raf);
        this.gl?.getExtension('WEBGL_lose_context')?.loseContext();
        this.stage.removeAttribute('data-stage-canvas');
      }

      // Once the stage is in view, after the page's load, when the browser is idle: never before the image.
      init() {
        if (this.started) return;
        this.started = true;
        const go = () => (window.requestIdleCallback || setTimeout)(() => this.build(), { timeout: 1000 });
        if (document.readyState === 'complete') go();
        else window.addEventListener('load', go, { once: true });
      }

      build() {
        // Without the stage CSS (not linked yet, or printing): no fixed box, no canvas.
        if (!this.isConnected || getComputedStyle(this).position !== 'fixed') return (this.started = false);
        if (!this.canvas) {
          this.canvas = document.createElement('canvas');
          this.canvas.addEventListener('webglcontextlost', (event) => {
            event.preventDefault();
            this.fail();
          });
          this.canvas.addEventListener('webglcontextrestored', () => this.program());
          this.append(this.canvas);
        }
        this.gl = this.canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false });
        if (this.gl) this.program();
      }

      program() {
        const gl = this.gl;
        const link = (source, names) => {
          const program = gl.createProgram();
          [
            [gl.VERTEX_SHADER, VERTEX],
            [gl.FRAGMENT_SHADER, source],
          ].forEach(([type, text]) => {
            const shader = gl.createShader(type);
            gl.shaderSource(shader, text);
            gl.compileShader(shader);
            gl.attachShader(program, shader);
          });
          gl.bindAttribLocation(program, 0, 'p');
          gl.linkProgram(program);
          if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
          const u = { program };
          ['uTime', ...names].forEach((name) => (u[name] = gl.getUniformLocation(program, name)));
          return u;
        };
        this.a = link(GRADIENT, ['uGrain', 'uProtect', 'uRes', ...TONES]);
        this.b = link(GRAIN, ['uTex', 'uNoise']);
        if (!this.a || !this.b) return this.fail();
        gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        // Pass 1's target: half float where it can be drawn into (skylrk's), so the grain can dip below 0.
        this.texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER].forEach((key) => gl.texParameteri(gl.TEXTURE_2D, key, gl.NEAREST));
        [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach((key) => gl.texParameteri(gl.TEXTURE_2D, key, gl.CLAMP_TO_EDGE));
        this.buffer = gl.createFramebuffer();
        gl.getExtension('EXT_color_buffer_half_float');
        this.type = gl.getExtension('OES_texture_half_float')?.HALF_FLOAT_OES;
        gl.useProgram(this.a.program);
        gl.uniform1f(this.a.uGrain, 0.1);
        gl.useProgram(this.b.program);
        gl.uniform1f(this.b.uNoise, 0.05);
        gl.uniform1i(this.b.uTex, 0);
        this.ready = true;
        this.drawn = false;
        this.clock = 0;
        this.width = 0;
        this.recolour(true);
        this.resize();
        this.update();
      }

      fail() {
        this.ready = false;
        cancelAnimationFrame(this.raf);
        this.removeAttribute('data-drawn');
        this.stage.removeAttribute('data-stage-canvas');
      }

      // The stage's colours (read through this element's CSS), as four tones.
      recolour(now) {
        if (!this.ready) return;
        const style = getComputedStyle(this);
        const glowX = parseFloat(style.getPropertyValue('--glow-x')) || 0.84;
        const glowY = parseFloat(style.getPropertyValue('--glow-y')) || 0.2;
        const key = [style.color, style.borderLeftColor, glowX, glowY].join();
        if (key === this.key) return;
        this.key = key;
        const [h, s, l] = hsl(rgb(style.color));
        const second = style.borderLeftColor !== style.color;
        // A light stage (ink text, --stage-on in the top border): no shade darker than the stage colour
        // in the bottom-left corner, where the label and the name sit (gradient-light-corner).
        this.protect = hsl(rgb(style.borderTopColor))[2] < 0.5 ? 1 : 0;
        this.to = [
          fromHsl(h, s * 0.9, l),
          fromHsl(h, s * 0.7, 0.18),
          fromHsl(h, s * 0.95, Math.max(l - 0.2, 0.22)),
          fromHsl(h, s * 0.85, Math.min(l + 0.25, 0.78)),
          second ? rgb(style.borderLeftColor) : rgb(style.color),
          [glowX, 1 - glowY, second ? 0.9 : 0],
        ];
        const moving = now !== true && this.from && this.running();
        this.from = moving ? this.tones : this.to;
        this.tones = this.from;
        this.since = performance.now();
        if (!this.running()) this.draw(performance.now());
      }

      resize() {
        if (!this.ready) return this.holds.has('away') || this.init();
        // The glow's place can change with the layout (the product stage's card from 750px).
        this.recolour();
        if (getComputedStyle(this).position !== 'fixed') return this.hold('wide', true);
        this.hold('wide', false);
        const gl = this.gl;
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        const width = Math.round(this.canvas.clientWidth * ratio);
        const height = Math.round(this.canvas.clientHeight * ratio);
        if (width === this.width && height === this.height) return;
        this.width = this.canvas.width = width;
        this.height = this.canvas.height = height;
        gl.viewport(0, 0, width, height);
        gl.useProgram(this.a.program);
        gl.uniform2f(this.a.uRes, width, height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.buffer);
        const target = (type) => {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, type, null);
          gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texture, 0);
          return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
        };
        if (!(this.type && target(this.type)) && !target(gl.UNSIGNED_BYTE)) return this.fail();
        if (!this.running()) this.draw(performance.now());
      }

      hold(reason, on) {
        if (on) this.holds.add(reason);
        else this.holds.delete(reason);
        this.update();
      }

      running() {
        return this.ready && !this.holds.size && !reducedMotionQuery.matches;
      }

      update() {
        cancelAnimationFrame(this.raf);
        this.last = 0;
        if (this.running()) this.raf = requestAnimationFrame(this.frame);
        else if (this.ready && !this.drawn && !this.holds.has('wide')) this.draw(performance.now());
      }

      frame(now) {
        this.raf = requestAnimationFrame(this.frame);
        if (this.last && now - this.last < 32) return;
        // A clock that only runs while drawing: no jump after a pause.
        this.clock += Math.min(now - (this.last || now), 100);
        this.last = now;
        this.draw(now);
      }

      draw(now) {
        if (!this.width) return;
        const gl = this.gl;
        const ease = Math.min(1, (now - this.since) / 600);
        const t = ease * ease * (3 - 2 * ease);
        // 1.35x the original pace (the owner, 2026-10-19: slightly faster).
        const time = 20 + (this.clock / 1000) * 1.35;
        this.tones = this.from.map((tone, i) => mix(tone, this.to[i], t));
        if (t === 1) this.from = this.to;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.buffer);
        gl.useProgram(this.a.program);
        this.tones.forEach((tone, i) => gl.uniform3fv(this.a[TONES[i]], tone));
        gl.uniform1f(this.a.uProtect, this.protect || 0);
        gl.uniform1f(this.a.uTime, time);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.useProgram(this.b.program);
        gl.uniform1f(this.b.uTime, time);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        if (this.drawn) return;
        this.drawn = true;
        this.setAttribute('data-drawn', '');
        this.stage.setAttribute('data-stage-canvas', '');
      }
    }
  );
}
